import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { FilterIcon } from '..';
import { FilterMultipleDropdownOverlay } from './FilterMultipleDropdownOverlay';
import type { PopperPlacementType } from '@material-ui/core/Popper';

import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';

export type OptionKey = string | number;

export enum DropdownComponent {
  CHECKBOX = 'CHECKBOX',
  CHIP = 'CHIP',
}

export interface CheckboxOption {
  key: OptionKey;
  label: string;
}

export interface FilterMultipleDropdownProps {
  size?: 'condensed' | 'regular';
  options: CheckboxOption[];
  selectedOptionKeys: Set<OptionKey>;
  isOpen?: boolean;
  overlayTitle?: string;
  buttonTitle?: string;
  buttonIcon?: React.ReactNode;
  cleanFilterTitle?: string;
  searchBoxPlaceholder?: string;
  noOptionsPlaceholder?: string;
  withActionButtons?: boolean;
  className?: string;
  overlayClassName?: string;
  onApply: (selectedOptions: CheckboxOption[]) => void;
  onDropdownClick?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  onClear?: () => void;
  renderComponent?: DropdownComponent;
  popperPlacementType?: PopperPlacementType;
  isDisabled?: boolean;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export const FilterMultipleDropdown: React.FC<FilterMultipleDropdownProps> = ({
  size = 'regular',
  options,
  selectedOptionKeys,
  overlayTitle,
  cleanFilterTitle,
  withActionButtons,
  buttonTitle,
  buttonIcon: ButtonIcon,
  searchBoxPlaceholder,
  noOptionsPlaceholder,
  className,
  overlayClassName,
  isOpen,
  renderComponent = DropdownComponent.CHECKBOX,
  popperPlacementType,
  isDisabled,
  ...props
}) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [selectionChanged, setSelectionChanged] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(() => {
    const optionsByKey = new Map<OptionKey, CheckboxOption>();
    options.forEach((option) => {
      if (selectedOptionKeys.has(option.key)) {
        optionsByKey.set(option.key, option);
      }
    });
    return optionsByKey;
  });

  useEffect(() => {
    setSelectedOptions(() => {
      const optionsByKey = new Map<OptionKey, CheckboxOption>();
      options.forEach((option) => {
        if (selectedOptionKeys.has(option.key)) {
          optionsByKey.set(option.key, option);
        }
      });
      return optionsByKey;
    });
  }, [options, selectedOptionKeys]);

  const applyFilter = useCallback(() => {
    props.onApply(Array.from(selectedOptions.values()));

    if (withActionButtons) {
      setForceClosed(true);
      setSelectionChanged(false);
    }
  }, [props, selectedOptions, withActionButtons]);

  const getButtonTitle = () => {
    const title = buttonTitle || 'Filtrar';
    const numSelected = selectedOptionKeys.size;

    if (numSelected > 0) {
      return `${title} (${numSelected})`;
    }
    return title;
  };

  const updateSelectionChanged = (optionsToBeApplied: CheckboxOption[]) => {
    const hasNotChanged = optionsToBeApplied.every((option) => selectedOptionKeys.has(option.key)) && optionsToBeApplied.length === selectedOptionKeys.size;
    setSelectionChanged(!hasNotChanged);
  };

  const handleOptionSelection = (option: CheckboxOption) => {
    const isCurrentlySelected = selectedOptions.has(option.key);
    const updatedSelected = new Map(selectedOptions);
    if (isCurrentlySelected) {
      updatedSelected.delete(option.key);
    } else {
      updatedSelected.set(option.key, option);
    }
    setSelectedOptions(updatedSelected);
    updateSelectionChanged(Array.from(updatedSelected.values()));
  };

  const clearFilters = () => {
    setSelectedOptions(new Map());
    updateSelectionChanged([]);

    if (props.onClear) {
      props.onClear();
    }
  };

  const onCancel = () => {
    setForceClosed((value) => !value);

    const updatedSelected = new Map();
    options.forEach((option) => {
      if (selectedOptionKeys.has(option.key)) {
        updatedSelected.set(option.key, option);
      }
    });
    setSelectedOptions(updatedSelected);
    updateSelectionChanged(Array.from(updatedSelected.values()));

    if (props.onCancel) {
      props.onCancel();
    }
  };

  return (
    <>
      <Dropdown
        onOpenChange={() => setForceClosed(false)}
        forceClose={forceClosed}
        overlay={
          <FilterMultipleDropdownOverlay
            className={overlayClassName}
            overlayTitle={overlayTitle}
            cleanFilterTitle={cleanFilterTitle}
            options={options}
            selectedOptionKeys={new Set(selectedOptions.keys())}
            selectionChanged={selectionChanged}
            onOptionSelection={handleOptionSelection}
            withActionButtons={withActionButtons}
            searchBoxPlaceholder={searchBoxPlaceholder}
            noOptionsPlaceholder={noOptionsPlaceholder}
            onClearFilter={props.onClear ? clearFilters : undefined}
            onApplyFilter={applyFilter}
            onCancel={onCancel}
            closeOverlayHandler={onCancel}
            renderComponent={renderComponent}
          />
        }
      >
        <Button size={size} variant='text' disabled={isDisabled} startIcon={ButtonIcon || <FilterIcon />}>
          {getButtonTitle()}
        </Button>
      </Dropdown>
    </>
  );
};
